import { useEffect, useState, useCallback, type ReactNode } from "react";
import { type User } from "../types";
import { apiService } from "../services/api";
import { authLogger } from "../utils/logger";
import { AuthContext, type AuthContextType } from "./AuthContext.types";
import { getUserPermissionsFromToken } from "../auth/claims";

// Export the AuthContextType for use in other files
export type { AuthContextType } from "./AuthContext.types";

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing session on mount
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (token) {
          // Try to get user profile from API
          const response = await apiService.getAuthProfile();

          // Get permissions from JWT token
          const tokenPermissions = getUserPermissionsFromToken(token);

          // Convert backend user format to frontend User format
          const user: User = {
            id: Date.now().toString(), // Temporary ID, should come from backend
            name: `${response.firstName} ${response.lastName}`,
            email: response.email,
            roles: response.roles.map((roleName: string, index: number) => ({
              id: index.toString(),
              name: roleName,
              permissions: tokenPermissions.map((permission, permIndex) => {
                const [resource, action] = permission.split(":");
                return {
                  id: permIndex.toString(),
                  resource,
                  action,
                };
              }),
            })),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          setUser(user);
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        // If API is not available, fall back to mock for development
        const token = localStorage.getItem("access_token");
        if (token && token.startsWith("mock-token-")) {
          const mockUsers = [
            {
              id: "1",
              name: "Admin User",
              email: "admin@igp.com",
              roles: [
                {
                  id: "1",
                  name: "admin",
                  permissions: [
                    { id: "1", resource: "users", action: "read" },
                    { id: "2", resource: "users", action: "manage" },
                    { id: "3", resource: "payments", action: "create" },
                    { id: "4", resource: "admin", action: "manage" },
                  ],
                },
              ],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            {
              id: "2",
              name: "Cliente Demo",
              email: "cliente@igp.com",
              roles: [
                {
                  id: "2",
                  name: "client",
                  permissions: [
                    { id: "5", resource: "payments", action: "create" },
                    { id: "6", resource: "profile", action: "read" },
                  ],
                },
              ],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            {
              id: "3",
              name: "Operador Demo",
              email: "operador@igp.com",
              roles: [
                {
                  id: "3",
                  name: "operator",
                  permissions: [
                    { id: "7", resource: "payments", action: "read" },
                    { id: "8", resource: "payments", action: "update" },
                  ],
                },
              ],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            {
              id: "4",
              name: "Usuario Demo",
              email: "usuario@igp.com",
              roles: [
                {
                  id: "4",
                  name: "user",
                  permissions: [
                    { id: "9", resource: "profile", action: "read" },
                  ],
                },
              ],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
          ];

          // Determine user based on token suffix
          let userIndex = 0;
          if (token.includes("client")) userIndex = 1;
          else if (token.includes("operator")) userIndex = 2;
          else if (token.includes("user")) userIndex = 3;

          setUser(mockUsers[userIndex]);
        } else {
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
        }
      } finally {
        setIsLoading(false);
      }
    };

    // Listen for logout events from interceptor
    const handleLogoutEvent = (event: CustomEvent) => {
      const reason = event.detail?.reason || "unknown";
      authLogger.warn("Logout triggered by interceptor", { reason });
      setUser(null);
      window.location.href = "/login";
    };

    window.addEventListener("auth:logout", handleLogoutEvent as EventListener);
    checkAuth();

    return () => {
      window.removeEventListener(
        "auth:logout",
        handleLogoutEvent as EventListener,
      );
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      // Try real API first
      const response = await apiService.login(email, password);
      if (response.success) {
        // Get permissions from JWT token
        const tokenPermissions = getUserPermissionsFromToken(response.token);

        // Convert backend user format to frontend User format
        const user: User = {
          id: Date.now().toString(), // Temporary ID, should come from backend
          name: `${response.user.firstName} ${response.user.lastName}`,
          email: response.user.email,
          roles: response.user.roles.map((roleName, index) => ({
            id: index.toString(),
            name: roleName,
            permissions: tokenPermissions.map((permission, permIndex) => {
              const [resource, action] = permission.split(":");
              return {
                id: permIndex.toString(),
                resource,
                action,
              };
            }),
          })),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setUser(user);
      } else {
        throw new Error("Login failed");
      }
    } catch (error) {
      authLogger.error("API login failed", error);

      // Re-throw API errors (validation, authentication, etc.)
      // These should be handled by the UI
      if (error && typeof error === "object" && "code" in error) {
        throw error;
      }

      // Only fallback to mock in development for network errors
      if (import.meta.env.DEV) {
        authLogger.warn("Falling back to mock data for development");
        const mockUsers = [
          {
            id: "1",
            name: "Admin User",
            email: "admin@igp.com",
            roles: [
              {
                id: "1",
                name: "admin",
                permissions: [
                  { id: "1", resource: "users", action: "read" },
                  { id: "2", resource: "users", action: "manage" },
                  { id: "3", resource: "payments", action: "create" },
                  { id: "4", resource: "admin", action: "manage" },
                ],
              },
            ],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          {
            id: "2",
            name: "Cliente Demo",
            email: "cliente@igp.com",
            roles: [
              {
                id: "2",
                name: "client",
                permissions: [
                  { id: "5", resource: "payments", action: "create" },
                  { id: "6", resource: "profile", action: "read" },
                ],
              },
            ],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          {
            id: "3",
            name: "Operador Demo",
            email: "operador@igp.com",
            roles: [
              {
                id: "3",
                name: "operator",
                permissions: [
                  { id: "7", resource: "payments", action: "read" },
                  { id: "8", resource: "payments", action: "update" },
                ],
              },
            ],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          {
            id: "4",
            name: "Usuario Demo",
            email: "usuario@igp.com",
            roles: [
              {
                id: "4",
                name: "user",
                permissions: [{ id: "9", resource: "profile", action: "read" }],
              },
            ],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        ];

        // Buscar usuario por email
        const user = mockUsers.find((u) => u.email === email);

        if (!user || password !== "123456") {
          throw new Error("Credenciales inválidas");
        }

        // Simular token con tipo de usuario
        const userType = user.roles[0]?.name || "admin";
        const token = `mock-token-${userType}-${Date.now()}`;
        localStorage.setItem("access_token", token);
        setUser(user);
      } else {
        throw new Error("Login failed");
      }
    }
  };

  const register = async (userData: Record<string, unknown>) => {
    try {
      // Try real API first
      const response = await apiService.register(userData);
      if (response.success) {
        // Get permissions from JWT token
        const tokenPermissions = getUserPermissionsFromToken(response.token);

        // Convert backend user format to frontend User format
        const user: User = {
          id: Date.now().toString(), // Temporary ID, should come from backend
          name: `${response.user.firstName} ${response.user.lastName}`,
          email: response.user.email,
          roles: response.user.roles.map((roleName, index) => ({
            id: index.toString(),
            name: roleName,
            permissions: tokenPermissions.map((permission, permIndex) => {
              const [resource, action] = permission.split(":");
              return {
                id: permIndex.toString(),
                resource,
                action,
              };
            }),
          })),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setUser(user);
      } else {
        throw new Error("Registration failed");
      }
    } catch (error) {
      authLogger.error("API registration failed", error);

      // Re-throw API errors (validation, authentication, etc.)
      // These should be handled by the UI
      if (error && typeof error === "object" && "code" in error) {
        throw error;
      }

      // Fallback to mock for development (only for network errors)
      const newUser: User = {
        id: Date.now().toString(),
        name: `${String(userData.firstName || "Usuario")} ${String(userData.lastName || "")}`,
        email: String(userData.email),
        roles: [{ id: "2", name: "client", permissions: [] }],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Simular token
      const token = "mock-token-" + Date.now();
      localStorage.setItem("access_token", token);
      setUser(newUser);
    }
  };

  const googleLogin = async (idToken: string) => {
    try {
      const response = await apiService.googleLogin(idToken);
      if (response.success) {
        // Get permissions from JWT token
        const tokenPermissions = getUserPermissionsFromToken(response.token);

        // Convert backend user format to frontend User format
        const user: User = {
          id: Date.now().toString(), // Temporary ID, should come from backend
          name: `${response.user.firstName} ${response.user.lastName}`,
          email: response.user.email,
          roles: response.user.roles.map((roleName, index) => ({
            id: index.toString(),
            name: roleName,
            permissions: tokenPermissions.map((permission, permIndex) => {
              const [resource, action] = permission.split(":");
              return {
                id: permIndex.toString(),
                resource,
                action,
              };
            }),
          })),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setUser(user);
      } else {
        throw new Error("Google login failed");
      }
    } catch (error) {
      authLogger.error("Google login failed", error);
      throw error;
    }
  };

  const logout = async (reason?: string) => {
    try {
      await apiService.logout();
    } catch (error) {
      console.warn("Logout API call failed:", error);
    } finally {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      setUser(null);
      authLogger.info("User logged out", { reason });
      window.location.href = "/login";
    }
  };

  const hasRole = (role: string): boolean => {
    return user?.roles?.some((r) => r.name === role) ?? false;
  };

  const hasRoles = (roles: string[]): boolean => {
    if (!user || !roles.length) return false;
    return roles.some((role) => user.roles?.some((r) => r.name === role));
  };

  const hasPermission = (resource: string, action: string): boolean => {
    if (!user) return false;
    return user.roles.some((role) =>
      role.permissions.some(
        (permission) =>
          permission.resource === resource && permission.action === action,
      ),
    );
  };

  const hasPermissions = (
    permissions: Array<{ resource: string; action: string }>,
  ): boolean => {
    if (!user || !permissions.length) return false;
    return permissions.every((permission) =>
      user.roles.some((role) =>
        role.permissions.some(
          (p) =>
            p.resource === permission.resource &&
            p.action === permission.action,
        ),
      ),
    );
  };

  const updateUser = async (userData: Partial<User>) => {
    try {
      const response = await apiService.updateUser(user?.id || "", userData);
      if (response.success) {
        setUser(response.data as unknown as User);
      }
    } catch (error) {
      authLogger.error("Update user failed", error);
      throw error;
    }
  };

  const getAccessToken = useCallback((): string | null => {
    return localStorage.getItem("access_token");
  }, []);

  const setTokens = useCallback(
    (tokens: { access: string; refresh: string }) => {
      localStorage.setItem("access_token", tokens.access);
      localStorage.setItem("refresh_token", tokens.refresh);
    },
    [],
  );

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    googleLogin,
    logout,
    hasRole,
    hasRoles,
    hasPermission,
    hasPermissions,
    updateUser,
    getAccessToken,
    setTokens,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
